import React, { useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FaSignInAlt } from "react-icons/fa";
import { useAuth } from "../contexts/AuthContext";
import { queueEvent } from "../analytics";
import { EVENTS } from "../analytics/events";
import { Button, Field, Input, PasswordInput } from "../ui";
import AuthLayout, { AuthLink } from "../components/site/AuthLayout";
import { DiscountNote } from "../components/site/DiscountBanner";
import { authCarry, authDestination } from "../utils/authRedirect";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Where to go after signing in, and what to carry to sign-up / reset.
  const carry = authCarry(location);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("البريد الإلكتروني وكلمة المرور مطلوبان");
      return;
    }
    setLoading(true);
    try {
      await login({ email: email.trim().toLowerCase(), password });
      queueEvent(EVENTS.AUTH_LOGIN_SUCCEEDED, { method: "email" });
      toast.success("تم تسجيل الدخول بنجاح");
      navigate(authDestination(location), { replace: true });
    } catch (error) {
      const message = error?.message || "";
      // Anonymous app accounts have no email -> backend returns "User Not Found!"
      if (message === "User Not Found!") {
        toast.error(
          "هذا الحساب غير مرتبط ببريد إلكتروني. اربط بريدك من التطبيق أولاً"
        );
      } else if (message === "Incorrect credintials!") {
        toast.error("البريد الإلكتروني أو كلمة المرور غير صحيحة");
      } else {
        toast.error(message || "حدث خطأ، حاول مرة أخرى");
      }
    } finally {
      setLoading(false);
    }
  };

  // Already signed in (e.g. opened /login from a bookmark): carry on.
  if (isAuthenticated && !loading) {
    return <Navigate to={authDestination(location)} replace />;
  }

  return (
    <AuthLayout
      title="تسجيل الدخول"
      subtitle="ادخل ببريدك وكلمة المرور لإدارة ألعابك واشتراكك"
      footer={
        <>
          ما عندك حساب؟{" "}
          <AuthLink to="/register" state={carry}>
            أنشئ حساباً جديداً
          </AuthLink>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <DiscountNote />

        <Field label="البريد الإلكتروني" htmlFor="email">
          <Input
            ltr
            type="email"
            id="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@email.com"
            required
            disabled={loading}
          />
        </Field>

        <Field label="كلمة المرور" htmlFor="password">
          <PasswordInput
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="كلمة المرور"
            required
            disabled={loading}
          />
          <div className="text-left pt-1">
            <Link
              to="/password-reset-request"
              state={carry}
              className="text-sm font-semibold text-white/70 hover:text-white"
            >
              نسيت كلمة المرور؟
            </Link>
          </div>
        </Field>

        <Button
          type="submit"
          size="lg"
          block
          loading={loading}
          disabled={!email || !password}
          icon={<FaSignInAlt />}
        >
          تسجيل الدخول
        </Button>

        <p className="text-xs text-white/55 text-center leading-relaxed">
          تلعب من التطبيق؟ اربط بريدك من حسابك في التطبيق، ثم ادخل هنا بنفس
          البريد.
        </p>
      </form>
    </AuthLayout>
  );
};

export default Login;
